import React, { forwardRef } from 'react';
import { Button as MuiButton } from '@mui/material';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!
**/
interface ClassKeyValue {
  [name: string]: string;
}

export interface ButtonProps {
  // Add any additional custom props here
  className?: string;
  variant?: 'text' | 'outlined' | 'contained';
  /**
   * `'default'` is kept as an alias of `'inherit'`: it was MUI v4's default colour and v5
   * removed it. Both map to `'inherit'`, which is also what an omitted `color` resolves to
   * here — see the `color` remap below.
   */
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
  startIcon?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  key?: React.Key;
  component?: React.ElementType;
  to?: string;
  disableRipple?: boolean;
  classes?: ClassKeyValue;
  from?: string;
  endIcon?: React.ReactNode;
  type?: 'submit' | 'reset' | 'button';
  size?: 'small' | 'medium' | 'large';
  autoFocus?: boolean;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  href?: string;
  children?: React.ReactNode;
  target?: React.HTMLAttributeAnchorTarget;
  disableElevation?: boolean;
  role?: React.AriaRole;
  /** For a button that opens a popover: the props `usePopoverState` returns as `triggerProps`. */
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
}

// MUI v4 defaulted `color` to `'default'` (palette.text.primary, no colour class); v5 defaults
// it to `'primary'`, which would repaint every button that passes no `color` in the theme's
// teal -- 24 of them on the claim Documents screen alone. `'inherit'` is v5's equivalent of the
// old default, so both the omitted value and the retired `'default'` literal resolve to it.
const toV5Color = (color: ButtonProps['color']): 'inherit' | 'primary' | 'secondary' =>
  color === undefined || color === 'default' ? 'inherit' : color;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ children, color, ...buttonProps }, ref) => {
  return (
    <MuiButton ref={ref} color={toV5Color(color)} {...buttonProps}>
      {children}
    </MuiButton>
  );
});

Button.displayName = 'Button';
export default Button;
